import { useCallback, useSyncExternalStore } from 'react';

/**
 * How far through a session a client is, and the check-in that ends it — held
 * **on the device and nowhere else**.
 *
 * ## What this is, said plainly
 *
 * There is no `session_completion` table and no `check_in` table. There is no
 * migration in `client-daily-loop` and no policy. So when a client marks an
 * exercise off, finishes a session, or tells their coach how it went, the only
 * thing that happens is that this module writes to that browser's own storage.
 *
 * **Nothing here is sent anywhere. No coach is notified. Nothing survives
 * reinstalling the app, clearing site data, or opening it on another phone.**
 * That is `client-daily-loop`'s decision D1, taken deliberately: adding the two
 * tables now would settle in a schema the questions the owner has explicitly
 * deferred — whether completion is per exercise or per session, whether a
 * check-in belongs to a session or to a week — and `migrations-forward-only`
 * makes a wrong shape on `main` expensive to correct.
 *
 * It is written here rather than left to be discovered because the screens above
 * this module are convincing, and a convincing screen with nothing behind it is
 * exactly the thing that gets mistaken for a working feature.
 *
 * ## Why it is not in the fixture module
 *
 * D1 says the fixtures hold the completions. They cannot, and the reason is
 * worth keeping: `src/shared/fixtures/queries.ts` mirrors `queries.real.ts`
 * function for function — `tests/unit/fixtureContract.test.ts` proves it in both
 * directions — and `queries.real.ts` can only hold queries against tables that
 * exist. A `listCompletions` there would have to answer from a table there is
 * none of. The alternative, importing the fixture module from a shared one,
 * would put invented people into every production bundle, which is the one thing
 * `demo-data` forbids outright.
 *
 * So completion lives here, in one module, identical in a fixtures build and in
 * a real one. No screen branches on the demo flag (`client-daily-loop` D6),
 * because there is nothing to branch on.
 *
 * ## Failing safe
 *
 * Every read and write is wrapped. `localStorage` throws in a private window
 * with site data blocked, and returns text that is not ours after a version
 * skew. Both produce an empty, unmarked session rather than a broken screen: the
 * worst case is a client who has to tick two boxes again, and the marking is
 * optional anyway.
 */

/** The five answers to "how did it feel", and no sixth. */
export const FEELINGS = ['great', 'good', 'ok', 'tough', 'rough'] as const;
export type Feeling = (typeof FEELINGS)[number];

/** The lowest and highest effort the check-in accepts, inclusive. */
export const EFFORT_MIN = 1;
export const EFFORT_MAX = 10;

export interface CheckIn {
  /** 1 to 10. Required. */
  effort: number;
  /** Required. */
  feeling: Feeling;
  /**
   * Whether anything hurt — or `null` for a client who did not opt in to being
   * asked, which is not the same as "no". The product never learns *where*, and
   * there is deliberately nowhere in this shape to put it.
   */
  hurt: boolean | null;
  /** Anything the client chose to add. Empty when they added nothing. */
  note: string;
  /** When it was submitted, as an ISO instant. */
  at: string;
}

export interface SessionProgress {
  /** The ids of the exercises marked done, in no particular order. */
  marked: string[];
  /** When the session was completed, or `null` while it is still in progress. */
  completedAt: string | null;
  /** The check-in that ended it, or `null`. */
  checkIn: CheckIn | null;
}

const EMPTY: SessionProgress = { marked: [], completedAt: null, checkIn: null };

/**
 * One key for everything, rather than one per session.
 *
 * Today has to ask "is today's session done" without knowing anything else about
 * the session, and a single record answers that in one read. The `v1` is what
 * lets a later shape be recognised and ignored rather than misread.
 */
const STORAGE_KEY = 'client.sessionProgress.v1';

type Store = Record<string, SessionProgress>;

function isProgress(value: unknown): value is SessionProgress {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Partial<SessionProgress>;
  return (
    Array.isArray(row.marked) &&
    row.marked.every((id) => typeof id === 'string') &&
    (row.completedAt === null || typeof row.completedAt === 'string')
  );
}

function read(): Store {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return {};
  }
  if (raw === null) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const store: Store = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (isProgress(value)) store[id] = value;
    }
    return store;
  } catch {
    return {};
  }
}

/*
 * Subscribers, so that Today and the session screen are never showing two
 * different answers about the same session in the same tab.
 */
const listeners = new Set<() => void>();

function write(store: Store): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // A browser that refuses to remember is a browser the client re-ticks a box
    // in. It is not a reason to fail the screen they are standing in front of.
  }
  for (const listener of listeners) listener();
}

/** What is known about one session. Never null: an unknown session is empty. */
export function readProgress(sessionId: string): SessionProgress {
  return read()[sessionId] ?? EMPTY;
}

/** Mark an exercise done, or unmark one marked by mistake. */
export function setMarked(sessionId: string, entryId: string, done: boolean): void {
  const store = read();
  const current = store[sessionId] ?? EMPTY;
  const marked = new Set(current.marked);
  if (done) marked.add(entryId);
  else marked.delete(entryId);

  store[sessionId] = { ...current, marked: [...marked] };
  write(store);
}

/**
 * Finish a session.
 *
 * The check-in is what completes it — there is no way to be done without one,
 * which is `mvp-discovery`'s "the check-in SHALL NOT be skippable".
 */
export function completeSession(sessionId: string, checkIn: CheckIn): void {
  const store = read();
  const current = store[sessionId] ?? EMPTY;
  store[sessionId] = { ...current, completedAt: checkIn.at, checkIn };
  write(store);
}

/** Forget everything about one session. Used by tests, and by nothing else. */
/**
 * Forget every answer to "did anything hurt", and keep everything else.
 *
 * This is what withdrawing the hurt consent actually does. The question is the
 * one health-adjacent thing this product asks, it is asked only of a client who
 * opted in, and the answer is the content stored under that consent — so
 * withdrawing has to remove it. The effort and the feeling are not stored under
 * it and stay: they are not health data and the client did not withdraw them.
 *
 * A session with no check-in at all is left exactly as it was.
 */
export function forgetHurtAnswers(): void {
  const store = read();
  const next: Store = {};
  for (const [id, value] of Object.entries(store)) {
    next[id] =
      value.checkIn === null ? value : { ...value, checkIn: { ...value.checkIn, hurt: null } };
  }
  write(next);
}

export function forgetProgress(sessionId: string): void {
  const store = read();
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete store[sessionId];
  write(store);
}

/**
 * One session's progress, re-rendered when it changes anywhere in this tab.
 *
 * `useSyncExternalStore` rather than `useState`, because two screens read the
 * same session: the client completes it on the session screen and Today has to
 * already know when they arrive back.
 *
 * The snapshot is cached per session id so that the identity is stable between
 * renders — returning a fresh object every time would loop.
 */
const snapshots = new Map<string, SessionProgress>();

function snapshotOf(sessionId: string): SessionProgress {
  const fresh = readProgress(sessionId);
  const held = snapshots.get(sessionId);
  if (
    held?.completedAt === fresh.completedAt &&
    held.marked.length === fresh.marked.length &&
    held.marked.every((id, index) => id === fresh.marked[index])
  ) {
    return held;
  }
  snapshots.set(sessionId, fresh);
  return fresh;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSessionProgress(sessionId: string): SessionProgress {
  const getSnapshot = useCallback(() => snapshotOf(sessionId), [sessionId]);
  // The server snapshot is the empty one: nothing is known about a session
  // before the device is asked, and there is no device during a prerender.
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
}
